// src/pages/insurance/InsuranceListPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";
import { useBreadcrumb } from "../../context/BreadcrumbContext";

import {
  getInsuranceParentDetails,
  deleteInsurance,
} from "../../api/insuranceApi";
import { getPropertyById } from "../../api/unitApi";
import { formatDateUk } from "../../utils/dateDisplay";

import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stack,
  Snackbar,
} from "@mui/material";

function InsuranceListPage() {
  const { propertyId, unitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useAuth();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();

  const [insuranceList, setInsuranceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const isUnitContext = Boolean(unitId);
  const userRoles = user?.claims_role || [];
  const canEdit = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("PUT")
  );
  const canCreate = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("POST")
  );
  const canDelete = userRoles.some((r) =>
    permissions?.[r?.toLowerCase?.()]?.includes("DELETE")
  );
  const showActions = canDelete;

  // Fetch insurance policies and set breadcrumbs
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getInsuranceParentDetails(propertyId, unitId);
        setInsuranceList(data.insurance_policies || []);

        // Set breadcrumb path
        if (isUnitContext) {
          // Unit context: Properties > PropertyName > UnitName > Insurance
          const propertyData = await getPropertyById(data.unit.property_id);
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: propertyData.name, path: `/properties/${data.unit.property_id}/details` },
            {
              label: `Unit ${data.unit.unit_name || data.unit.unit_code || data.unit.id}`,
              path: `/units/${unitId}/details`,
            },
            { label: "Insurance", path: null },
          ]);
        } else {
          // Property context: Properties > PropertyName > Insurance
          setCustomPath([
            { label: "Properties", path: "/properties" },
            { label: data.name, path: `/properties/${propertyId}/details` },
            { label: "Insurance", path: null },
          ]);
        }
      } catch (err) {
        setError("Failed to load insurance policies.");
      } finally {
        setLoading(false);
      }
    };
    load();

    return () => clearCustomPath();
  }, [propertyId, unitId, isUnitContext, setCustomPath, clearCustomPath]);

  useEffect(() => {
    const incomingToast = location.state?.toast;
    if (!incomingToast?.message) return;
    showToast(incomingToast.message, incomingToast.severity || "success");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const handleAdd = () => {
    navigate(
      isUnitContext
        ? `/units/${unitId}/insurance/create`
        : `/properties/${propertyId}/insurance/create`
    );
  };

  const handleEdit = (id) => {
    navigate(
      isUnitContext
        ? `/units/${unitId}/insurance/${id}/edit`
        : `/properties/${propertyId}/insurance/${id}/edit`
    );
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this policy?")) return;

    try {
      await deleteInsurance(id);
      setInsuranceList((prev) => prev.filter((p) => p.id !== id));
      showToast("Insurance policy deleted");
    } catch {
      showToast("Failed to delete policy.", "error");
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box className="page-container" sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box className="page-container">
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          {isUnitContext
            ? "Unit Insurance Policies"
            : "Property Insurance Policies"}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {canCreate && (
          <Button variant="contained" sx={{ mb: 2 }} onClick={handleAdd}>
            + Add Policy
          </Button>
        )}

        {insuranceList.length === 0 ? (
          <Typography>No insurance policies found.</Typography>
        ) : (
          <Paper elevation={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Policy #</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Insurance Type</TableCell>
                    <TableCell>Coverage</TableCell>
                    <TableCell>Renewal Date</TableCell>
                    <TableCell>Premium (£)</TableCell>
                    {showActions && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {insuranceList.map((p) => (
                    <TableRow
                      key={p.id}
                      hover={canEdit}
                      onClick={() => {
                        if (canEdit) {
                          handleEdit(p.id);
                        }
                      }}
                      sx={{ cursor: canEdit ? "pointer" : "default" }}
                    >
                      <TableCell>{p.policy_number}</TableCell>
                      <TableCell>{p.provider_name}</TableCell>
                      <TableCell>{p.insurance_type || "-"}</TableCell>
                      <TableCell>{p.coverage_type}</TableCell>
                      <TableCell>{formatDateUk(p.renewal_date)}</TableCell>
                      <TableCell>{p.premium_amount}</TableCell>

                      {showActions && (
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            {canDelete && (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDelete(p.id);
                                }}
                              >
                                Delete
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default InsuranceListPage;
